import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, reactive, onMounted, Ref, ComponentPublicInstance } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useAuthStore, useUiStore } from '@/stores';
import { useVuelidateWithLogging } from '@/composables/useVuelidateWithLogging';
import { goBack } from '@/composables/useNavigation';
import { ContactService, SelectOptionsService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';
import { required, maxLength } from '@/utils/validators';

import {
    Permission,
    FormAction,
    ContactModel,
    ContactFormState,
    SelectOptionsServiceGetUsersResult,
} from '@/types';

import { ContactEmailForm, ContactPhoneForm } from '@/views/pages/contact/components';


export default /*@__PURE__*/_defineComponent({
  __name: 'ContactFormPage',
  props: {
    action: { type: String, default: FormAction.CREATE },
},
  setup(__props, { expose: __expose }) {
  __expose();

const props = __props;

const route = useRoute();
const uiStore = useUiStore();
const authenticatedUserId = useAuthStore().user?.id;

const emailRefs = ref<Record<string, InstanceType<typeof ContactEmailForm> | null>>({});
const phoneRefs = ref<Record<string, InstanceType<typeof ContactPhoneForm> | null>>({});
const usersOptions: Ref<SelectOptionsServiceGetUsersResult[]> = ref([]);

const formState = reactive<ContactFormState>({
    name: null,
    emails: [],
    phones: [
        {
            number: null,
            _key: crypto.randomUUID(),
            isMain: true,
        },
    ],
    contactCompany: null,
    ownerUserId: authenticatedUserId ?? null,
});

const rules = computed(() => ({
    name: { required, maxLength: maxLength(255) },
    emails: {},
    phones: { required },
    contactCompany: {},
    ownerUserId: { required },
}));

const { v$, $validateWithLogging } = useVuelidateWithLogging(rules, formState);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const fetchUsers = async () => {
    try {
        const { data } = await SelectOptionsService.getUsers({
            params: {
                is_active: true,
            },
        });

        usersOptions.value = data;
    } catch (error) {
        toastServiceError(error);
    }
};

const fetchContact = async (id: number) => {
    try {
        await setLoading(true);
        const { data } = await ContactService.get<ContactModel>(id);
        formState.name = data.name;
        formState.emails = data.emails.map((email) => ({ ...email, _key: crypto.randomUUID() }));
        formState.phones = data.phones.map((phone) => ({ ...phone, _key: crypto.randomUUID() }));
        formState.contactCompany = data.contactCompany;
        formState.ownerUserId = data.ownerUserId;
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

const setEmailRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        emailRefs.value[key] = el as InstanceType<typeof ContactEmailForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete emailRefs.value[key];
    }
};

const setPhoneRef = (el: Element | ComponentPublicInstance | null, key: string) => {
    if (el && '$el' in el) {
        phoneRefs.value[key] = el as InstanceType<typeof ContactPhoneForm>;
    } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        delete phoneRefs.value[key];
    }
};

const addEmail = () => {
    formState.emails.push({
        email: null,
        _key: crypto.randomUUID(),
        isMain: formState.emails.length ? false : true,
    });
};

const addPhone = () => {
    formState.phones.push({ number: null, _key: crypto.randomUUID(), isMain: false });
};

const removeEmail = (index: number) => {
    const [removedEmail] = formState.emails.splice(index, 1);
    if (removedEmail.isMain && formState.emails.length > 0) formState.emails[0].isMain = true;
};

const removePhone = (index: number) => {
    if (formState.phones.length <= 1) return;

    const [removedPhone] = formState.phones.splice(index, 1);

    if (removedPhone.isMain) formState.phones[0].isMain = true;
};

const setMainPhone = (key: string) => {
    formState.phones.forEach((phone) => {
        phone.isMain = phone._key === key;
    });
};

const setMainEmail = (key: string) => {
    formState.emails.forEach((email) => {
        email.isMain = email._key === key;
    });
};

const onSubmit = async () => {
    const emailValidationResults = await Promise.all(
        Object.values(emailRefs.value).map((ref) => !ref || ref?.validate?.()),
    );

    const phoneValidationResults = await Promise.all(
        Object.values(phoneRefs.value).map((ref) => ref?.validate?.() ?? false),
    );

    const allValid = [...emailValidationResults, ...phoneValidationResults].every(Boolean);
    const isFormValid = await $validateWithLogging();

    if (!isFormValid || !allValid) return;

    await setLoading(true);

    const payload = {
        ...formState,
        emails: formState.emails.map(({ _key, ...email }) => email),
        phones: formState.phones.map(({ _key, ...phone }) => phone),
    };

    const handleServiceSuccess = (message: string, id: number) => {
        toast.success(message);
        v$.value.$reset();
        goBack({ name: 'ViewContactById', params: { id } });
    };

    try {
        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            const { data }: AxiosResponse = await ContactService.update(id, payload);
            handleServiceSuccess(data.message, id);
        } else {
            const { data }: AxiosResponse = await ContactService.create(payload);
            handleServiceSuccess(data.message, data.result.id);
        }
    } catch (e) {
        toastServiceError(e);
    } finally {
        await setLoading(false);
    }
};

onMounted(async () => {
    await fetchUsers();
    if (props.action === FormAction.EDIT) {
        const contactId = Number(route.params.id);
        if (!isNaN(contactId)) fetchContact(contactId);
    }
});

const __returned__ = { props, route, uiStore, authenticatedUserId, emailRefs, phoneRefs, usersOptions, formState, rules, v$, $validateWithLogging, setLoading, fetchUsers, fetchContact, setEmailRef, setPhoneRef, addEmail, addPhone, removeEmail, removePhone, setMainPhone, setMainEmail, onSubmit, get Permission() { return Permission }, get FormAction() { return FormAction }, get ContactEmailForm() { return ContactEmailForm }, get ContactPhoneForm() { return ContactPhoneForm } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})